import router from "@/router";
import Course from "@/types/Course";
import { CourseListItem } from "@/types/Course";
import Lesson from "@/types/Lesson";
import Section from "@/types/Section";
import User from "@/types/User";
import UserProgress from "@/types/UserProgress";
import { getToken, getRequest, postRequest, postFormRequest } from "@/utils/api";
import { AssignmentSlide } from "@/types/Section";
import { createStore } from "vuex";
import Alert from "@/types/Alert";
import { Tip } from "@/types/Tip";

type State = {
  authToken: string | null;
  isAuthenticated: boolean;
  user: User | null;
  userProgress: UserProgress | null;
  courses: CourseListItem[];
  course: Course | null;
  currentLesson: Lesson | null;
  currentSection: Section | null;
  currentSlide: string | null;
  currentTips: Tip[];
  alerts: Alert[];
};

export default createStore({
  state: {
    authToken: null,
    isAuthenticated: false,
    user: null,
    userProgress: null,
    courses: [],
    course: null,
    currentLesson: null,
    currentSection: null,
    currentSlide: null,
    currentTips: [],
    alerts: [],
  } as State,
  mutations: {
    setToken(state, token) {
      state.authToken = token;
      state.isAuthenticated = true;
    },

    removeToken(state) {
      state.authToken = null;
      state.isAuthenticated = false;
    },

    setUser(state, user) {
      state.user = user;
    },

    setUserProgress(state, userProgress) {
      state.userProgress = userProgress;
    },

    setCourses(state, courses) {
      state.courses = courses;
    },

    setCourse(state, course) {
      state.course = course;
    },

    setCurrentLesson(state, lesson) {
      state.currentLesson = lesson;
    },

    setCurrentSection(state, section) {
      state.currentSection = section;
    },

    setCurrentSlide(state, slide) {
      state.currentSlide = slide;
    },

    setCurrentTips(state, tips) {
      state.currentTips = tips;
    },

    addAlert(state, alert) {
      state.alerts.push(alert);
    },

    removeAlert(state, alertType) {
      // set alerts state to everything that isn't of the indicated alertType
      state.alerts = state.alerts.filter(
        (alert) => alert.alertType !== alertType
      );
    },
  },
  actions: {
    async logIn(context, { username, password }) {
      let response;
      try {
        response = await getToken(username, password);
      } catch (error) {
        // fetch() rejects (rather than resolving a non-2xx) when the request never completes —
        // DNS failure, TLS/certificate problem on the API host, server down, CORS block. Without
        // this the rejection went unhandled and the login form just did nothing.
        context.commit("addAlert", {
          alert: "Could not reach the server. Please try again in a moment.",
          alertType: "login",
        });
        throw error;
      }

      if (response.token) {
        context.commit("setToken", response.token);
        localStorage.setItem("token", response.token);
        context.commit("removeAlert", "login");
        router.push(
          response.must_change_password
            ? { name: "ChangePassword" }
            : { name: "Home" }
        );
      } else {
        context.commit("addAlert", {
          alert: "Sorry, could not log in",
          alertType: "login",
        });
        throw new Error("bad login");
      }
    },

    async logInWithToken(context, token) {
      if (token) {
        context.commit("setToken", token);
        router.push({ name: "Home" });
      } else {
        throw new Error("bad login"); // TODO should replace this with something else to tell user login failed
      }
    },

    async logOut(context) {
      context.commit("removeToken");
      localStorage.removeItem("token");
      router.push({ name: "Login" });
    },

    // TODO add loading and error handling
    async getUser(context) {
      if (context.state.authToken) {
        const result = await getRequest<User>(
          "whoami",
          context.state.authToken
        );
        context.commit("setUser", result);
      }
    },

    // TODO add loading and error handling
    async getUserProgress(context) {
      if (context.state.authToken) {
        const result = await getRequest<UserProgress>(
          "me/progress",
          context.state.authToken
        );
        context.commit("setUserProgress", result);
      }
    },

    async fetchCourses(context) {
      if (context.state.authToken) {
        const result = await getRequest<CourseListItem[]>(
          "courses",
          context.state.authToken
        );
        context.commit("setCourses", result);
      }
    },

    async getCourse(context, id) {
      if (context.state.authToken) {
        const result = await getRequest<Course>(
          `courses/${id}`,
          context.state.authToken
        );
        context.commit("setCourse", result);
      }
    },

    async getCurrentLesson(context, id) {
      if (context.state.authToken) {
        const result = await getRequest<Lesson>(
          `lessons/${id}`,
          context.state.authToken
        );
        context.commit("setCurrentLesson", result);
      }
    },

    async getCurrentSection(context, id) {
      if (context.state.authToken) {
        const result = await getRequest<Section>(
          `sections/${id}`,
          context.state.authToken
        );
        context.commit("setCurrentSection", result);
      }
    },

    async getCurrentTips(context, id) {
      if (context.state.authToken) {
        const result = await getRequest<Tip[]>(
          `tips?slide_id=${id}`,
          context.state.authToken
        );
        context.commit("setCurrentTips", result);
        context.commit("setCurrentSlide", id);
      }
    },

    // TODO add way to handle errors from the server
    async updateTips(context, body) {
      if (context.state.authToken) {
        await postRequest<Record<string, unknown>>(
          `tips`,
          {
            body: body.body,
            slide_id: context.state.currentSlide,
          },
          context.state.authToken
        );
      }
    },

    // returns whether the chosen answer was correct, for the caller to show feedback
    async checkQuizAnswer(context, { slideId, choiceIndex }) {
      if (context.state.authToken) {
        const result = await postRequest<{ correct: boolean }>(
          `slides/${slideId}/check-answer`,
          { choice_index: choiceIndex },
          context.state.authToken
        );
        return result.correct;
      }
      return false;
    },

    // returns whether the change succeeded, for the caller to redirect on success. postRequest
    // never rejects on non-2xx (see utils/api.ts), so a failed change (wrong current password)
    // comes back as {detail: "..."} with no token — branch on that the same way App.vue
    // branches on a missing `username` for a failed whoami.
    async changePassword(context, { currentPassword, newPassword }) {
      if (context.state.authToken) {
        const result = await postRequest<{ token?: string }>(
          "change-password",
          { current_password: currentPassword, new_password: newPassword },
          context.state.authToken
        );
        if (result.token) {
          context.commit("setToken", result.token);
          localStorage.setItem("token", result.token);
          context.commit("removeAlert", "password");
          return true;
        }
        context.commit("addAlert", {
          alert: "Could not change password — check your current password",
          alertType: "password",
        });
        return false;
      }
      return false;
    },

    // returns the updated slide (submitted/submitted_filename/submitted_at) for the caller to
    // update local UI state directly, no follow-up GET needed — mirrors checkQuizAnswer's
    // "no store mutation, caller consumes the return value" pattern.
    async submitAssignment(context, { slideId, file }) {
      if (context.state.authToken) {
        const formData = new FormData();
        formData.append("file", file);
        const result = await postFormRequest<AssignmentSlide>(
          `slides/${slideId}/submit-assignment`,
          formData,
          context.state.authToken
        );
        return result;
      }
      return null;
    },

    // returns whether the server recorded it, so the caller can move on (or show an error).
    // postRequest never rejects on non-2xx (see utils/api.ts), so success means {ok: true}.
    async markSectionComplete(context) {
      if (context.state.authToken && context.state.currentSection) {
        try {
          const result = await postRequest<{ ok?: boolean }>(
            `sections/${context.state.currentSection.id}/complete`,
            {},
            context.state.authToken
          );
          return result.ok === true;
        } catch (error) {
          return false; // network failure
        }
      }
      return false;
    },
  },

  modules: {},
});
